import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import HashtagSelector from '../../../shared/components/HashtagSelector';
import { useTranslation } from 'react-i18next';

type MediaPost = {
  _id: string;
  type: 'media';
  media: string[];
  hashtags?: string[];
  userId?: string;
  createdAt: string;
};

const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';
const VIDEO_CDN = process.env.REACT_APP_PUBLIC_VIDEO_BASE_URL || '';

const resolveMediaUrl = (url: string): string => {
  if (!url) return url;

  // already absolute
  if (/^https?:\/\//i.test(url)) return url;

  const clean = url.replace(/^\//, '');

  // basic video detection (matches your usage here)
  const isVideo = /\.(mp4|webm|mov|m4v)$/i.test(clean);

  const base = isVideo ? VIDEO_CDN : IMAGE_CDN;
  return `${base.replace(/\/$/, '')}/${clean}`;
};



const GalleryPostEditing = () => {
  const { token, user: authUser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [post, setPost] = useState<MediaPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMedia, setEditMedia] = useState<string[]>([]);
  const [editHashtags, setEditHashtags] = useState<string[]>([]);
  const normalizeTags = (input: string[] | string): string[] => {
    const pieces = Array.isArray(input) ? input : [input];
    return Array.from(
      new Set(
        pieces
          .flatMap(s => String(s).split(/[,\s#]+/)) // split commas, spaces, stray #
          .map(s => s.trim().replace(/^#/, '').toLowerCase())
          .filter(Boolean)
      )
    );
  };


  useEffect(() => {
    if (!id) {
      setLoading(false);
      alert(t('galleryPostEditing.errors.invalidUrl'));
      return;
    }    

    (async () => {
      try {
        const res = await api.get(`/post-editing/media/${id}`);

        const fetched: MediaPost = res.data;

        if (fetched.type !== 'media') {
          alert(t('galleryPostEditing.errors.notMedia'));
          navigate(-1);
          return;
        }
        if (fetched.userId && fetched.userId !== authUser?._id) {
          alert(t('galleryPostEditing.errors.notOwner'));
          navigate(-1);
          return;
        }

        setPost(fetched);
        setEditMedia(fetched.media || []);
        setEditHashtags(normalizeTags(fetched.hashtags || []));
      } catch (e) {
        console.error('❌ Failed to load post:', e);
        alert(t('galleryPostEditing.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token, authUser, navigate]);

  const handleRemoveMedia = (index: number) => {
    setEditMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!post?._id) return alert(t('galleryPostEditing.errors.notLoaded'));
    setSaving(true);
    try {
      const mediaToSave = editMedia.map(u => u.replace(/\?.*$/, ''));
await api.put(
  `/post-editing/media/${post._id}`,
  { media: mediaToSave, hashtags: editHashtags, status: 'published' }
);
      alert(t('galleryPostEditing.success.updated'));
      navigate('/my-postings');
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || err?.message;
      console.error('❌ Failed to save post:', status, msg, err);
      if (status === 401) {
        alert(t('galleryPostEditing.errors.save401'));
      } else {
        alert(
          t('galleryPostEditing.errors.saveGeneric', {
            message: msg || '',
          })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);
// put this near the top of the file

  return (
    <div style={pageShell}>
      <aside style={sidebarStyle}>
        <h2 style={{ margin: 0, marginTop: '2%', marginBottom: '6%' }}>
        {t('galleryPostEditing.title')}
        </h2>

        <div style={sidebarContent}>
          <div style={hashtagsBox}>
          <HashtagSelector
  context="gallery"
  selectedTags={editHashtags}
  onChange={(val) => setEditHashtags(normalizeTags(val))}
/>


        </div>
        </div>


        <div style={sidebarFooter}>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              ...primaryBtn,
              backgroundColor: saving ? '#4b2a78' : '#5c2d91',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving
    ? t('galleryPostEditing.buttons.saving')
    : `💾 ${t('galleryPostEditing.buttons.saveChanges')}`}
</button>
          <button onClick={handleCancel} style={secondaryBtn}>❌ {t('galleryPostEditing.buttons.cancelEdit')}</button>
        </div>
      </aside>

      <main style={mainStyle}>
        {loading || !post ? (
          <p>{t('galleryPostEditing.loading')}</p>
        ) : (
          <div style={postCardStyle}>
            <div style={horizontalMediaScrollStyle}>
              {editMedia.length === 0 ? (
                <p style={{ color: '#888', margin: 0 }}>{t('galleryPostEditing.noMedia')}</p>
              ) : (
                editMedia.map((url, index) => {
                  const fullUrl = resolveMediaUrl(url);
                  const isVid = /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(fullUrl || '');
                  const key = `${index}-${url}`;

                  return (
                    <div key={key} style={{ position: 'relative', flex: '0 0 auto' }}>
                      {isVid ? (
                        <video src={fullUrl} controls style={mediaItem} />
                      ) : (
                        <img src={fullUrl} alt={`media-${index}`} style={mediaItem} />
                      )}
                      <button onClick={() => handleRemoveMedia(index)} style={deleteChip}>✖</button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

/* Styles */
const MEDIA_TILE_HEIGHT = '60vh';
const mediaItem: React.CSSProperties = {
  height: MEDIA_TILE_HEIGHT,
  borderRadius: 12,
  display: 'block',
};
const pageShell: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  height: '100vh',
  background: '#000',
  color: '#bbb',
};
const sidebarStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '16%',
  height: '100vh',
  padding: 24,
  background: '#000',
  color: '#bbb',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  gap: 12,
  overflow: 'hidden',
  boxShadow: '2px 0 5px rgba(0,0,0,0.4)',
  zIndex: 10,
};
const sidebarContent: React.CSSProperties = {
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const sidebarFooter: React.CSSProperties = {
  paddingTop: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginBottom: 'min(8vh, 30%)',
  
};

const hashtagsBox: React.CSSProperties = {
  paddingRight: 4,
  borderRadius: 6,
 
};
const mainStyle: React.CSSProperties = {
  marginLeft: '21%',
  width: '77%',
  height: '100vh',
  overflowY: 'hidden',
  padding: '24px',
  boxSizing: 'border-box',
};
const postCardStyle: React.CSSProperties = {
  background: '#111',
  border: '1px solid #444',
  borderRadius: 12,
  padding: 20,
  color: '#ccc',
  boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: '4%',
}; 
const horizontalMediaScrollStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: 20,
  paddingBottom: 10,
  paddingTop: 10,
  // ❌ remove the line below (it makes the top look larger)
  // marginTop: '3%',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};
const deleteChip: React.CSSProperties = {
  position: 'absolute',
  top: 6,
  right: 6,
  background: 'rgba(0,0,0,0.6)',
  border: 'none',
  color: '#fff',
  borderRadius: '50%',
  width: 30,
  height: 30,
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  color: '#fff',
  borderRadius: 8,
  padding: '10px 20px',
  fontSize: 16,
  border: 'none',
};
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: '#666',
  cursor: 'pointer',
};


export default GalleryPostEditing;
